import { useTranslation } from "react-i18next";

interface LocalBusinessProps {
  type?: "LocalBusiness" | "EntertainmentAgency";
}

export const LocalBusinessSchema = ({ type = "EntertainmentAgency" }: LocalBusinessProps) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || "en";

  const schema = {
    "@context": "https://schema.org",
    "@type": type,
    name: "First Stage Talent",
    alternateName: "First Stage Voiceover & Acting Agency",
    description: currentLang === "fr"
      ? "Agence de talents de voix et d'acteurs professionnels au Canada, spécialisée dans la narration, les publicités et la production médiatique."
      : "Professional voiceover and acting talent agency in Canada, specializing in narration, commercials, and media production.",
    url: "https://firststagetalent.com",
    logo: "https://firststagetalent.com/favicon.png",
    image: "https://firststagetalent.com/og-image.jpg",
    telephone: "+1-613-447-7435",
    email: "agent@firststage.ca",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Ottawa-Gatineau",
      addressRegion: "ON",
      addressCountry: "CA",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 45.4215,
      longitude: -75.6972,
    },
    areaServed: [
      { "@type": "Country", name: "Canada" },
      { "@type": "Country", name: "United States" },
    ],
    serviceType: [
      "Voiceover Casting",
      "On-Camera Talent",
      "Production Support",
      "Photography",
      "Video Production",
      "Language Services",
    ],
    knowsLanguage: ["en", "fr"],
    priceRange: "$$",
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
    sameAs: [
      // Add social media links when available
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

interface PersonSchemaProps {
  name: string;
  description?: string;
  image?: string;
  jobTitle?: string;
  url?: string;
}

export const PersonSchema = ({ name, description, image, jobTitle, url }: PersonSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name,
    description,
    image,
    jobTitle,
    url,
    worksFor: {
      "@type": "Organization",
      name: "First Stage Talent",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  items: FAQItem[];
}

export const FAQSchema = ({ items }: FAQSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

interface ServiceSchemaProps {
  name: string;
  description: string;
  provider?: string;
}

export const ServiceSchema = ({ name, description, provider = "First Stage Talent" }: ServiceSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    provider: {
      "@type": "Organization",
      name: provider,
    },
    areaServed: {
      "@type": "Country",
      name: "Canada",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};
